import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import labService from '../../../services/lab.service';
import SubHeader from '../../layout/components/SubHeader';
import Pagination from '../../common/Pagination';
import OpticalDispensarySkeleton from './subparts/OpticalDispensarySkeleton';
import PickupSearchForm from './PickupSearchForm';
import OpticalDispensaryToolbar from './OpticalDispensaryToolbar';
import ClOrderRequestSearchForm from './ClOrderRequestSearchForm';
import ClOrderRequestToolbar from './ClOrderRequestToolbar';
import {
    DEFAULT_PICKUP_FILTERS,
    DEFAULT_PICKUP_PAGE_SIZE,
    DEFAULT_PICKUP_SEARCH_COLUMN,
    DEFAULT_PICKUP_SORT,
    buildPickupsQuery,
    formatPickupCount,
    formatPickupDateTime,
    formatPickupPatientName,
    getPickupStatusMeta,
    getPickupTypeMeta,
} from './subparts/pickupQuery';
import {
    DEFAULT_CL_ORDER_FILTERS,
    DEFAULT_CL_ORDER_PAGE_SIZE,
    DEFAULT_CL_ORDER_SEARCH_COLUMN,
    DEFAULT_CL_ORDER_SORT,
    buildClOrderRequestsQuery,
    formatClOrderAddress,
    formatClOrderCount,
    formatClOrderDateTime,
    formatClOrderPatientName,
    getClOrderStatusMeta,
    getClOrderTypeMeta,
    CL_ORDER_STATUS_VALUES,
} from './subparts/clOrderRequestQuery';
import './OpticalDispensary.css';

function OpticalDispensary() {
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);
    const location = useLocation();
    const isClOrders = location.pathname.includes('/cl-orders');

    const [pickupRecords, setPickupRecords] = useState([]);
    const [pickupLoading, setPickupLoading] = useState(!isClOrders);
    const [pickupSearchPerformed, setPickupSearchPerformed] = useState(false);
    const [pickupShowAll, setPickupShowAll] = useState(false);
    const [pickupSearchTerm, setPickupSearchTerm] = useState('');
    const [pickupSearchColumn, setPickupSearchColumn] = useState(DEFAULT_PICKUP_SEARCH_COLUMN);
    const [pickupFilters, setPickupFilters] = useState(DEFAULT_PICKUP_FILTERS);
    const [pickupSortBy, setPickupSortBy] = useState(DEFAULT_PICKUP_SORT.sortBy);
    const [pickupSortOrder, setPickupSortOrder] = useState(DEFAULT_PICKUP_SORT.sortOrder);
    const [pickupPageNumber, setPickupPageNumber] = useState(0);
    const [pickupPageSize, setPickupPageSize] = useState(DEFAULT_PICKUP_PAGE_SIZE);
    const [pickupTotalPages, setPickupTotalPages] = useState(0);
    const [pickupTotalElements, setPickupTotalElements] = useState(0);

    const [clRecords, setClRecords] = useState([]);
    const [clLoading, setClLoading] = useState(isClOrders);
    const [clSearchPerformed, setClSearchPerformed] = useState(false);
    const [clShowAll, setClShowAll] = useState(false);
    const [clSearchTerm, setClSearchTerm] = useState('');
    const [clSearchColumn, setClSearchColumn] = useState(DEFAULT_CL_ORDER_SEARCH_COLUMN);
    const [clFilters, setClFilters] = useState(DEFAULT_CL_ORDER_FILTERS);
    const [clSortBy, setClSortBy] = useState(DEFAULT_CL_ORDER_SORT.sortBy);
    const [clSortOrder, setClSortOrder] = useState(DEFAULT_CL_ORDER_SORT.sortOrder);
    const [clPageNumber, setClPageNumber] = useState(0);
    const [clPageSize, setClPageSize] = useState(DEFAULT_CL_ORDER_PAGE_SIZE);
    const [clTotalPages, setClTotalPages] = useState(0);
    const [clTotalElements, setClTotalElements] = useState(0);

    const loadPickups = useCallback(
        async ({
            nextSearchColumn = pickupSearchColumn || DEFAULT_PICKUP_SEARCH_COLUMN,
            nextSearchTerm = pickupSearchTerm,
            nextFilters = pickupFilters,
            nextSortBy = pickupSortBy || DEFAULT_PICKUP_SORT.sortBy,
            nextSortOrder = pickupSortOrder || DEFAULT_PICKUP_SORT.sortOrder,
            nextPageSize = pickupPageSize || DEFAULT_PICKUP_PAGE_SIZE,
            nextPageNo = 0,
            nextShowAll = pickupShowAll,
        } = {}) => {
            if (!clinicId) {
                return;
            }

            setPickupLoading(true);

            try {
                const query = buildPickupsQuery(
                    {
                        searchColumn: nextSearchColumn,
                        searchTerm: nextSearchTerm,
                        showAll: nextShowAll,
                        status: nextFilters.status,
                        type: nextFilters.type,
                        isConfirmed: nextFilters.isConfirmed,
                    },
                    {
                        sortBy: nextSortBy,
                        sortOrder: nextSortOrder,
                    }
                );

                const res = await labService.getPickups(clinicId, nextPageNo, nextPageSize, query);
                const data = res.data || {};

                setPickupRecords(data.content || []);
                setPickupPageNumber(data.number ?? nextPageNo);
                setPickupPageSize(data.size ?? nextPageSize);
                setPickupTotalPages(data.totalPages ?? 0);
                setPickupTotalElements(data.totalElements ?? 0);
                setPickupSearchTerm(nextShowAll ? '' : nextSearchTerm);
                setPickupSearchColumn(nextSearchColumn);
                setPickupFilters(nextFilters);
                setPickupSortBy(nextSortBy);
                setPickupSortOrder(nextSortOrder);
                setPickupShowAll(nextShowAll);
                setPickupSearchPerformed(true);
            } catch (err) {
                console.error('Failed to load pickup confirmations', err);
                toast.error('Failed to load pickup confirmations');
                setPickupRecords([]);
                setPickupTotalPages(0);
                setPickupTotalElements(0);
            } finally {
                setPickupLoading(false);
            }
        },
        [
            clinicId,
            pickupSearchColumn,
            pickupSearchTerm,
            pickupFilters,
            pickupSortBy,
            pickupSortOrder,
            pickupPageSize,
            pickupShowAll,
        ]
    );

    const loadClOrders = useCallback(
        async ({
            nextSearchColumn = clSearchColumn || DEFAULT_CL_ORDER_SEARCH_COLUMN,
            nextSearchTerm = clSearchTerm,
            nextFilters = clFilters,
            nextSortBy = clSortBy || DEFAULT_CL_ORDER_SORT.sortBy,
            nextSortOrder = clSortOrder || DEFAULT_CL_ORDER_SORT.sortOrder,
            nextPageSize = clPageSize || DEFAULT_CL_ORDER_PAGE_SIZE,
            nextPageNo = 0,
            nextShowAll = clShowAll,
        } = {}) => {
            if (!clinicId) {
                return;
            }

            setClLoading(true);

            try {
                const query = buildClOrderRequestsQuery(
                    {
                        searchColumn: nextSearchColumn,
                        searchTerm: nextSearchTerm,
                        showAll: nextShowAll,
                        type: nextFilters.type,
                        status: nextFilters.status,
                    },
                    {
                        sortBy: nextSortBy,
                        sortOrder: nextSortOrder,
                    }
                );

                const res = await labService.getClOrderRequests(clinicId, nextPageNo, nextPageSize, query);
                const data = res.data || {};

                setClRecords(data.content || []);
                setClPageNumber(data.number ?? nextPageNo);
                setClPageSize(data.size ?? nextPageSize);
                setClTotalPages(data.totalPages ?? 0);
                setClTotalElements(data.totalElements ?? 0);
                setClSearchTerm(nextShowAll ? '' : nextSearchTerm);
                setClSearchColumn(nextSearchColumn);
                setClFilters(nextFilters);
                setClSortBy(nextSortBy);
                setClSortOrder(nextSortOrder);
                setClShowAll(nextShowAll);
                setClSearchPerformed(true);
            } catch (err) {
                console.error('Failed to load CL order requests', err);
                toast.error('Failed to load CL order requests');
                setClRecords([]);
                setClTotalPages(0);
                setClTotalElements(0);
            } finally {
                setClLoading(false);
            }
        },
        [clinicId, clSearchColumn, clSearchTerm, clFilters, clSortBy, clSortOrder, clPageSize, clShowAll]
    );

    useEffect(() => {
        if (!clinicId) {
            return;
        }

        if (isClOrders) {
            loadClOrders({
                nextSearchTerm: '',
                nextSearchColumn: DEFAULT_CL_ORDER_SEARCH_COLUMN,
                nextFilters: DEFAULT_CL_ORDER_FILTERS,
                nextSortBy: DEFAULT_CL_ORDER_SORT.sortBy,
                nextSortOrder: DEFAULT_CL_ORDER_SORT.sortOrder,
                nextPageSize: DEFAULT_CL_ORDER_PAGE_SIZE,
                nextPageNo: 0,
                nextShowAll: true,
            });
            return;
        }

        loadPickups({
            nextSearchTerm: '',
            nextSearchColumn: DEFAULT_PICKUP_SEARCH_COLUMN,
            nextFilters: DEFAULT_PICKUP_FILTERS,
            nextSortBy: DEFAULT_PICKUP_SORT.sortBy,
            nextSortOrder: DEFAULT_PICKUP_SORT.sortOrder,
            nextPageSize: DEFAULT_PICKUP_PAGE_SIZE,
            nextPageNo: 0,
            nextShowAll: true,
        });
        // Load once when entering the page (or when clinicId becomes available).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isClOrders, clinicId]);

    const handlePickupSearch = (column, term) => {
        loadPickups({
            nextSearchColumn: column || DEFAULT_PICKUP_SEARCH_COLUMN,
            nextSearchTerm: term,
            nextPageNo: 0,
            nextShowAll: false,
        });
    };

    const handlePickupShowAll = () => {
        loadPickups({
            nextSearchTerm: '',
            nextSearchColumn: DEFAULT_PICKUP_SEARCH_COLUMN,
            nextPageNo: 0,
            nextShowAll: true,
        });
    };

    const handlePickupClearSearch = () => {
        loadPickups({
            nextSearchTerm: '',
            nextSearchColumn: DEFAULT_PICKUP_SEARCH_COLUMN,
            nextFilters: DEFAULT_PICKUP_FILTERS,
            nextSortBy: DEFAULT_PICKUP_SORT.sortBy,
            nextSortOrder: DEFAULT_PICKUP_SORT.sortOrder,
            nextPageSize: DEFAULT_PICKUP_PAGE_SIZE,
            nextPageNo: 0,
            nextShowAll: true,
        });
    };

    const handlePickupStatusChange = (value) => {
        if (!pickupSearchPerformed) {
            setPickupFilters((prev) => ({ ...prev, status: value }));
            return;
        }
        loadPickups({ nextFilters: { ...pickupFilters, status: value }, nextPageNo: 0 });
    };

    const handlePickupTypeChange = (value) => {
        if (!pickupSearchPerformed) {
            setPickupFilters((prev) => ({ ...prev, type: value }));
            return;
        }
        loadPickups({ nextFilters: { ...pickupFilters, type: value }, nextPageNo: 0 });
    };

    const handlePickupConfirmedChange = (value) => {
        if (!pickupSearchPerformed) {
            setPickupFilters((prev) => ({ ...prev, isConfirmed: value }));
            return;
        }
        loadPickups({ nextFilters: { ...pickupFilters, isConfirmed: value }, nextPageNo: 0 });
    };

    const handlePickupSortByChange = (value) => {
        if (!pickupSearchPerformed) {
            setPickupSortBy(value);
            return;
        }
        loadPickups({ nextSortBy: value, nextPageNo: 0 });
    };

    const handlePickupSortOrderChange = (value) => {
        if (!pickupSearchPerformed) {
            setPickupSortOrder(value);
            return;
        }
        loadPickups({ nextSortOrder: value, nextPageNo: 0 });
    };

    const handlePickupPageSizeChange = (value) => {
        if (!pickupSearchPerformed) {
            setPickupPageSize(value);
            return;
        }
        loadPickups({ nextPageSize: value, nextPageNo: 0 });
    };

    const handleClSearch = (column, term) => {
        loadClOrders({
            nextSearchColumn: column || DEFAULT_CL_ORDER_SEARCH_COLUMN,
            nextSearchTerm: term,
            nextPageNo: 0,
            nextShowAll: false,
        });
    };

    const handleClShowAll = () => {
        loadClOrders({
            nextSearchTerm: '',
            nextSearchColumn: DEFAULT_CL_ORDER_SEARCH_COLUMN,
            nextPageNo: 0,
            nextShowAll: true,
        });
    };

    const handleClClearSearch = () => {
        loadClOrders({
            nextSearchTerm: '',
            nextSearchColumn: DEFAULT_CL_ORDER_SEARCH_COLUMN,
            nextFilters: DEFAULT_CL_ORDER_FILTERS,
            nextSortBy: DEFAULT_CL_ORDER_SORT.sortBy,
            nextSortOrder: DEFAULT_CL_ORDER_SORT.sortOrder,
            nextPageSize: DEFAULT_CL_ORDER_PAGE_SIZE,
            nextPageNo: 0,
            nextShowAll: true,
        });
    };

    const handleClTypeChange = (value) => {
        if (!clSearchPerformed) {
            setClFilters((prev) => ({ ...prev, type: value }));
            return;
        }
        loadClOrders({ nextFilters: { ...clFilters, type: value }, nextPageNo: 0 });
    };

    const handleClStatusChange = (value) => {
        if (!clSearchPerformed) {
            setClFilters((prev) => ({ ...prev, status: value }));
            return;
        }
        loadClOrders({ nextFilters: { ...clFilters, status: value }, nextPageNo: 0 });
    };

    const handleClOrderStatusUpdate = async (orderId, status) => {
        if (!clinicId || !orderId || !status) {
            return;
        }

        try {
            const res = await labService.updateClOrderRequestStatus(clinicId, orderId, status);
            const updated = res.data || {};
            setClRecords((prev) =>
                prev.map((order) => (order.id === orderId ? { ...order, status: updated.status || status } : order))
            );
            toast.success('CL order status updated');
        } catch (err) {
            console.error('Failed to update CL order status', err);
            toast.error('Failed to update CL order status');
        }
    };

    const handleClSortByChange = (value) => {
        if (!clSearchPerformed) {
            setClSortBy(value);
            return;
        }
        loadClOrders({ nextSortBy: value, nextPageNo: 0 });
    };

    const handleClSortOrderChange = (value) => {
        if (!clSearchPerformed) {
            setClSortOrder(value);
            return;
        }
        loadClOrders({ nextSortOrder: value, nextPageNo: 0 });
    };

    const handleClPageSizeChange = (value) => {
        if (!clSearchPerformed) {
            setClPageSize(value);
            return;
        }
        loadClOrders({ nextPageSize: value, nextPageNo: 0 });
    };

    const handleClRefresh = async () => {
        if (!clinicId || clLoading) {
            return;
        }

        await loadClOrders({
            nextPageNo: clSearchPerformed ? clPageNumber : 0,
            nextShowAll: clSearchPerformed ? clShowAll : true,
        });
    };

    const handlePickupRefresh = async () => {
        if (!clinicId || pickupLoading) {
            return;
        }

        await loadPickups({
            nextPageNo: pickupSearchPerformed ? pickupPageNumber : 0,
            nextShowAll: pickupSearchPerformed ? pickupShowAll : true,
        });
    };

    const description = useMemo(() => {
        if (isClOrders) {
            if (!clSearchPerformed) {
                return 'Search and browse patient contact lens reorder requests';
            }
            const total = clTotalElements ?? 0;
            return `${formatClOrderCount(total)} CL order request${total === 1 ? '' : 's'} loaded`;
        }

        if (!pickupSearchPerformed) {
            return 'Search and browse glasses and contact lens pickup confirmations';
        }

        const total = pickupTotalElements ?? 0;
        return `${formatPickupCount(total)} pickup confirmation${total === 1 ? '' : 's'} loaded`;
    }, [isClOrders, pickupSearchPerformed, pickupTotalElements, clSearchPerformed, clTotalElements]);

    return (
        <div className="ecx-page optical-dispensary-page ecx-page--full-width">
            <SubHeader
                title={isClOrders ? 'CL Orders' : 'Pickups'}
                description={description}
                icon={isClOrders ? 'bi-eye' : 'bi-box-seam'}
                iconTheme="violet"
                containerClass="w-100 px-4"
            >
                {isClOrders ? (
                    <button
                        type="button"
                        className="btn btn-light-primary btn-sm rounded-pill"
                        onClick={handleClRefresh}
                        disabled={clLoading}
                        title="Refresh CL order requests"
                    >
                        <i className={`bi bi-arrow-clockwise me-1 ${clLoading ? 'ecx-refresh-spin' : ''}`} />
                        Refresh
                    </button>
                ) : (
                    <button
                        type="button"
                        className="btn btn-light-primary btn-sm rounded-pill"
                        onClick={handlePickupRefresh}
                        disabled={pickupLoading}
                        title="Refresh pickup confirmations"
                    >
                        <i className={`bi bi-arrow-clockwise me-1 ${pickupLoading ? 'ecx-refresh-spin' : ''}`} />
                        Refresh
                    </button>
                )}
            </SubHeader>

            <div className="ecx-page-body">
                {!isClOrders ? (
                    <div className="ecx-page-card ecx-page-panel optical-dispensary-panel">
                        <PickupSearchForm
                            key={pickupSearchPerformed ? 'pickup-results' : 'pickup-ready'}
                            onSearch={handlePickupSearch}
                            onShowAll={handlePickupShowAll}
                            isLoading={pickupLoading}
                            searchTerm={pickupSearchTerm}
                            searchColumn={pickupSearchColumn || DEFAULT_PICKUP_SEARCH_COLUMN}
                            status={pickupFilters.status}
                            type={pickupFilters.type}
                            isConfirmed={pickupFilters.isConfirmed}
                            sortBy={pickupSortBy || DEFAULT_PICKUP_SORT.sortBy}
                            sortOrder={pickupSortOrder || DEFAULT_PICKUP_SORT.sortOrder}
                            pageSize={pickupPageSize || DEFAULT_PICKUP_PAGE_SIZE}
                            onStatusChange={handlePickupStatusChange}
                            onTypeChange={handlePickupTypeChange}
                            onConfirmedChange={handlePickupConfirmedChange}
                            onSortByChange={handlePickupSortByChange}
                            onSortOrderChange={handlePickupSortOrderChange}
                            onPageSizeChange={handlePickupPageSizeChange}
                        />

                        {pickupSearchPerformed && (
                            <div className="ecx-page-panel__header border-top-0 optical-dispensary-toolbar-wrap">
                                <OpticalDispensaryToolbar
                                    isLoading={pickupLoading}
                                    searchTerm={pickupSearchTerm}
                                    searchColumn={pickupSearchColumn}
                                    showAll={pickupShowAll}
                                    status={pickupFilters.status}
                                    type={pickupFilters.type}
                                    isConfirmed={pickupFilters.isConfirmed}
                                    onClearSearch={handlePickupClearSearch}
                                />
                            </div>
                        )}

                        <div className="ecx-page-panel__body ecx-page-scroll">
                            {!pickupSearchPerformed && !pickupLoading ? (
                                <div className="optical-dispensary-empty">
                                    <span className="optical-dispensary-empty__icon">
                                        <i className="bi bi-eyeglasses" />
                                    </span>
                                    <h5 className="optical-dispensary-empty__title">Ready to Find Pickups</h5>
                                    <p className="optical-dispensary-empty__text">
                                        Search by patient name, PMS ID, type, or status using the filters above,
                                        or browse all pickup confirmations for your clinic.
                                    </p>
                                    <button
                                        type="button"
                                        className="btn btn-light-primary"
                                        onClick={handlePickupShowAll}
                                        disabled={pickupLoading}
                                    >
                                        <i className="bi bi-collection me-1" />
                                        Show All Pickups
                                    </button>
                                </div>
                            ) : pickupLoading ? (
                                <OpticalDispensarySkeleton />
                            ) : pickupRecords.length === 0 ? (
                                <div className="ecx-page-empty py-5">
                                    <span className="ecx-page-empty__icon">
                                        <i className="bi bi-eyeglasses" />
                                    </span>
                                    <h5 className="mb-2 text-dark">No pickup confirmations found</h5>
                                    <p className="mb-0 text-muted">
                                        Try another search field, a shorter term, or clear filters and show all.
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <div className="table-responsive">
                                        <table className="table ecx-page-table align-middle mb-0">
                                            <thead>
                                                <tr>
                                                    <th scope="col">Patient</th>
                                                    <th scope="col">Type</th>
                                                    <th scope="col">Status</th>
                                                    <th scope="col">PMS</th>
                                                    <th scope="col">Sent</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {pickupRecords.map((pickup) => {
                                                    const statusMeta = getPickupStatusMeta(pickup.status);
                                                    const typeMeta = getPickupTypeMeta(pickup.type);
                                                    const patientName = formatPickupPatientName(pickup);

                                                    return (
                                                        <tr key={pickup.id}>
                                                            <td>
                                                                {pickup.patientId ? (
                                                                    <Link
                                                                        to={`/patient-files/${pickup.patientId}`}
                                                                        className="fw-normal text-decoration-none"
                                                                    >
                                                                        {patientName}
                                                                    </Link>
                                                                ) : (
                                                                    <span className="fw-normal">{patientName}</span>
                                                                )}
                                                                {pickup.isConfirmed === true && (
                                                                    <div className="small text-success mt-1">
                                                                        <i className="bi bi-check2-circle me-1" />
                                                                        Patient confirmed
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <span className="optical-dispensary-type">
                                                                    <i className={`bi ${typeMeta.icon}`} />
                                                                    {typeMeta.label}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span
                                                                    className={`optical-pickup-status ${statusMeta.className}`}
                                                                >
                                                                    <i className={`bi ${statusMeta.icon}`} />
                                                                    {statusMeta.label}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <div className="small text-muted">
                                                                    {pickup.prmStatus || pickup.prmType || '—'}
                                                                </div>
                                                                {(pickup.prmId || pickup.prmPickupId) && (
                                                                    <div className="small text-muted">
                                                                        {[pickup.prmId, pickup.prmPickupId]
                                                                            .filter(Boolean)
                                                                            .join(' · ')}
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <div className="small">
                                                                    {formatPickupDateTime(pickup.createdAt)}
                                                                </div>
                                                                {pickup.modifiedAt &&
                                                                    pickup.modifiedAt !== pickup.createdAt && (
                                                                        <div className="small text-muted">
                                                                            Updated{' '}
                                                                            {formatPickupDateTime(pickup.modifiedAt)}
                                                                        </div>
                                                                    )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>

                                    <Pagination
                                        currentPage={pickupPageNumber}
                                        totalPages={pickupTotalPages}
                                        onPageChange={(newPage) => loadPickups({ nextPageNo: newPage })}
                                        totalElements={pickupTotalElements}
                                        pageSize={pickupPageSize}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="ecx-page-card ecx-page-panel optical-dispensary-panel">
                        <ClOrderRequestSearchForm
                            key={clSearchPerformed ? 'cl-results' : 'cl-ready'}
                            onSearch={handleClSearch}
                            onShowAll={handleClShowAll}
                            isLoading={clLoading}
                            searchTerm={clSearchTerm}
                            searchColumn={clSearchColumn || DEFAULT_CL_ORDER_SEARCH_COLUMN}
                            type={clFilters.type}
                            status={clFilters.status}
                            sortBy={clSortBy || DEFAULT_CL_ORDER_SORT.sortBy}
                            sortOrder={clSortOrder || DEFAULT_CL_ORDER_SORT.sortOrder}
                            pageSize={clPageSize || DEFAULT_CL_ORDER_PAGE_SIZE}
                            onTypeChange={handleClTypeChange}
                            onStatusChange={handleClStatusChange}
                            onSortByChange={handleClSortByChange}
                            onSortOrderChange={handleClSortOrderChange}
                            onPageSizeChange={handleClPageSizeChange}
                        />

                        {clSearchPerformed && (
                            <div className="ecx-page-panel__header border-top-0 optical-dispensary-toolbar-wrap">
                                <ClOrderRequestToolbar
                                    isLoading={clLoading}
                                    searchTerm={clSearchTerm}
                                    searchColumn={clSearchColumn}
                                    showAll={clShowAll}
                                    type={clFilters.type}
                                    status={clFilters.status}
                                    onClearSearch={handleClClearSearch}
                                />
                            </div>
                        )}

                        <div className="ecx-page-panel__body ecx-page-scroll">
                            {!clSearchPerformed && !clLoading ? (
                                <div className="optical-dispensary-empty">
                                    <span className="optical-dispensary-empty__icon">
                                        <i className="bi bi-eye" />
                                    </span>
                                    <h5 className="optical-dispensary-empty__title">Ready to Find CL Orders</h5>
                                    <p className="optical-dispensary-empty__text">
                                        Search by name, email, phone, brand, or fulfillment type,
                                        or browse all contact lens reorder requests for your clinic.
                                    </p>
                                    <button
                                        type="button"
                                        className="btn btn-light-primary"
                                        onClick={handleClShowAll}
                                        disabled={clLoading}
                                    >
                                        <i className="bi bi-collection me-1" />
                                        Show All CL Orders
                                    </button>
                                </div>
                            ) : clLoading ? (
                                <OpticalDispensarySkeleton />
                            ) : clRecords.length === 0 ? (
                                <div className="ecx-page-empty py-5">
                                    <span className="ecx-page-empty__icon">
                                        <i className="bi bi-eye" />
                                    </span>
                                    <h5 className="mb-2 text-dark">No CL order requests found</h5>
                                    <p className="mb-0 text-muted">
                                        Try another search field, a shorter term, or clear filters and show all.
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <div className="table-responsive">
                                        <table className="table ecx-page-table align-middle mb-0">
                                            <thead>
                                                <tr>
                                                    <th scope="col">Requester</th>
                                                    <th scope="col">Brand / Supply</th>
                                                    <th scope="col">Fulfillment</th>
                                                    <th scope="col">Status</th>
                                                    <th scope="col">Contact</th>
                                                    <th scope="col">Requested</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {clRecords.map((order) => {
                                                    const typeMeta = getClOrderTypeMeta(order.type);
                                                    const statusMeta = getClOrderStatusMeta(order.status);
                                                    const patientName = formatClOrderPatientName(order);
                                                    const address = formatClOrderAddress(order);

                                                    return (
                                                        <tr key={order.id}>
                                                            <td>
                                                                {order.patientId ? (
                                                                    <Link
                                                                        to={`/patient-files/${order.patientId}`}
                                                                        className="fw-normal text-decoration-none"
                                                                    >
                                                                        {patientName}
                                                                    </Link>
                                                                ) : (
                                                                    <span className="fw-normal">{patientName}</span>
                                                                )}
                                                                {order.comments && (
                                                                    <div className="small text-muted mt-1 text-truncate optical-cl-order-comments">
                                                                        {order.comments}
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <div className="fw-normal">
                                                                    {order.brandName || '—'}
                                                                </div>
                                                                <div className="small text-muted">
                                                                    {order.quantity || '—'}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <span
                                                                    className={`optical-dispensary-type optical-cl-order-type ${typeMeta.className}`}
                                                                >
                                                                    <i className={`bi ${typeMeta.icon}`} />
                                                                    {typeMeta.label}
                                                                </span>
                                                                {order.type === 'DELIVERY' && address && (
                                                                    <div className="small text-muted mt-1">
                                                                        {address}
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <div className="d-flex flex-column gap-1">
                                                                    <span
                                                                        className={`optical-cl-order-status ${statusMeta.className}`}
                                                                    >
                                                                        <i className={`bi ${statusMeta.icon}`} />
                                                                        {statusMeta.label}
                                                                    </span>
                                                                    <select
                                                                        className="form-select form-select-sm optical-cl-order-status-select"
                                                                        value={order.status || 'PENDING'}
                                                                        onChange={(event) =>
                                                                            handleClOrderStatusUpdate(
                                                                                order.id,
                                                                                event.target.value
                                                                            )
                                                                        }
                                                                        disabled={clLoading}
                                                                        aria-label={`Update status for ${patientName}`}
                                                                    >
                                                                        {CL_ORDER_STATUS_VALUES.map((option) => (
                                                                            <option
                                                                                key={option.value}
                                                                                value={option.value}
                                                                            >
                                                                                {option.label}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="small">{order.email || '—'}</div>
                                                                {(order.phoneNumber || order.cellPhone) && (
                                                                    <div className="small text-muted">
                                                                        {order.phoneNumber || order.cellPhone}
                                                                    </div>
                                                                )}
                                                                {order.homePhone && (
                                                                    <div className="small text-muted">
                                                                        Home: {order.homePhone}
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <div className="small">
                                                                    {formatClOrderDateTime(order.createdAt)}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>

                                    <Pagination
                                        currentPage={clPageNumber}
                                        totalPages={clTotalPages}
                                        onPageChange={(newPage) => loadClOrders({ nextPageNo: newPage })}
                                        totalElements={clTotalElements}
                                        pageSize={clPageSize}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default OpticalDispensary;
